// Kiosk API.
//
// The waiting-room tablet holds no staff credential. The patient's phone number,
// verified server-side, is what unlocks a short-lived token scoped to their one
// visit ("phone-as-passkey").
//
// Previously the roster call returned entire visit documents — including
// `patient.cellPhone`, which React then compared in the browser. That both
// disclosed every patient's number and made the check bypassable from devtools.
// Verification now happens on the server and the number never leaves it.

import axios from "axios";
import { BASE_URL, normalizeError } from "./client";

// A bare axios instance: kiosk calls must never pick up a staff token from
// localStorage, and a 401 here means "confirm your phone again", not "log out".
const kioskClient = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// Same normalisation as the staff client, but a 401 here means "confirm your
// phone number again" rather than "log out", so no session handling is attached.
kioskClient.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(normalizeError(error))
);

/**
 * Today's roster. The clinic day is resolved server-side, and blocked,
 * rescheduled, no-show and signed-out visits are already filtered out — the
 * client no longer needs to do any of that itself.
 */
export async function fetchToday() {
  const { data } = await kioskClient.get("/kiosk/today");
  return data;
}

/**
 * Exchange a visit id and phone number for a visit-scoped token.
 *
 * On failure the thrown ApiError carries:
 *   code PHONE_MISMATCH → details.attemptsRemaining
 *   code VISIT_LOCKED   → status 429, details.retryAt
 *
 * A staff token, when supplied, bypasses the per-visit lockout — that is how the
 * front desk signs in a patient who has locked themselves out.
 */
export async function verifyPhone(visitId, phone, staffToken, stationToken) {
  const headers = {};
  // A staff token bypasses the per-visit lockout; a station token elevates the
  // minted kiosk token to allow reassessment prefill. They are independent, and
  // either, both or neither may be present.
  if (staffToken) headers.Authorization = `Bearer ${staffToken}`;
  if (stationToken) headers["X-Kiosk-Station"] = `Bearer ${stationToken}`;

  const { data } = await kioskClient.post(
    "/kiosk/verify",
    { visitId, phone },
    Object.keys(headers).length ? { headers } : undefined
  );
  return data;
}

const withToken = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

/** Sign in. The visit and the timestamp both come from the server. */
export async function signIn(token, signature) {
  const { data } = await kioskClient.post(
    "/kiosk/signin",
    { signature },
    withToken(token)
  );
  return data;
}

export async function signOut(token, signature) {
  const { data } = await kioskClient.post(
    "/kiosk/signout",
    { signature },
    withToken(token)
  );
  return data;
}

/**
 * Identity and contact fields only.
 *
 * The clinical block is deliberately absent: behind a phone-number gate it would
 * expose a full health history, including communicable-disease status. Callers
 * must treat every clinical field as undefined.
 */
export async function fetchIntake(token) {
  const { data } = await kioskClient.get("/kiosk/intake", withToken(token));
  return data;
}

export async function submitIntake(token, general) {
  const { data } = await kioskClient.post(
    "/kiosk/intake",
    { general },
    withToken(token)
  );
  return data;
}

/**
 * The prior assessment, for a returning patient completing a reassessment.
 *
 * Only succeeds when the kiosk token was minted with a station present (the
 * `clinical` claim) AND the visit is a reassessment; otherwise the server
 * returns 403 and the caller falls back to the identity-only prefill. Unlike
 * fetchIntake this deliberately DOES carry the clinical block — that is the
 * whole point, and it is why it is gated behind an established station.
 */
export async function fetchReassessment(token) {
  const { data } = await kioskClient.get("/kiosk/reassessment", withToken(token));
  return data;
}

// The kiosk token is scoped to one visit and expires in 30 minutes. It lives in
// sessionStorage so it dies with the tab and never persists on a shared tablet.
// The reassessment flag rides along so the intake form knows whether to attempt
// a clinical prefill.
const TOKEN_KEY = "kioskToken";
const VISIT_KEY = "kioskVisitId";
const REASSESSMENT_KEY = "kioskReassessment";

export function storeSession(token, visitId, reassessment = false) {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(VISIT_KEY, visitId);
  if (reassessment) sessionStorage.setItem(REASSESSMENT_KEY, "1");
  else sessionStorage.removeItem(REASSESSMENT_KEY);
}

export function readSession() {
  return {
    token: sessionStorage.getItem(TOKEN_KEY),
    visitId: sessionStorage.getItem(VISIT_KEY),
    reassessment: sessionStorage.getItem(REASSESSMENT_KEY) === "1",
  };
}

export function clearSession() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(VISIT_KEY);
  sessionStorage.removeItem(REASSESSMENT_KEY);
}

// The kiosk *station* token is a device-level credential, not a per-visit one.
// It is long-lived (30 days) and must persist across tabs and reloads on the
// tablet, so it lives in localStorage — but under a key distinct from the staff
// `token`, so the shared api/client never attaches it and the tablet is never
// treated as a staff session.
const STATION_TOKEN_KEY = "kioskStationToken";

/** Establish a station. No staff Authorization header — this is the credential exchange itself. */
export async function stationLogin(email, password) {
  const { data } = await kioskClient.post("/kiosk/station", { email, password });
  return data;
}

/** Ask the server whether a station token is still valid. */
export async function stationStatus(token) {
  const { data } = await kioskClient.get("/kiosk/station", withToken(token));
  return data;
}

export function storeStationToken(token) {
  localStorage.setItem(STATION_TOKEN_KEY, token);
}

export function readStationToken() {
  return localStorage.getItem(STATION_TOKEN_KEY);
}

export function clearStationToken() {
  localStorage.removeItem(STATION_TOKEN_KEY);
}

/**
 * The station token's `exp`, as ms since epoch, decoded WITHOUT verifying the
 * signature — enough for the badge to avoid showing "online" for a token that
 * has plainly expired. The server remains the real authority. Returns null when
 * the token is absent or unparseable.
 */
export function stationTokenExpiry(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json);
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch (error) {
    return null;
  }
}
